import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	ReactNode,
} from 'react';
import { useLoad } from './sw.hooks';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('sw-context');

// Only external content images (CDN-hosted article/issue art), not our own
// same-origin app-shell assets -- those are already Workbox-precached.
//
// Deliberately not filtering by URL extension: this only runs against real
// <img> elements already rendered in the DOM (see collectAndSend below), so
// "is this an image" is already established by the DOM itself. A CDN that
// serves images via extensionless or query-string-based URLs (resize/format
// params, no trailing .jpg/.png) would silently never get forwarded to the
// SW for caching if we re-validated the URL shape here.
function isExternalContentImage(src: string): boolean {
	try {
		const url = new URL(src, window.location.href);
		if (url.protocol !== 'http:' && url.protocol !== 'https:') {
			return false;
		}
		return url.origin !== window.location.origin;
	} catch {
		return false;
	}
}

export type ServiceWorkerMessage =
	| {
			type: 'DOWNLOAD_BACKGROUND_IMAGES';
			payload: {
				images: Array<string>;
				IssueID?: string | number;
				priority?: 'high' | 'low';
				// Per-URL issue ownership, for batches spanning more than one
				// issue (e.g. a catalogue page's covers) where a single
				// top-level IssueID can't express it. Keyed by the same
				// string that appears in `images`. Entries with no owner
				// here and no top-level IssueID are cached but untracked --
				// evictIssue() can't reclaim them individually, only a full
				// cache wipe does.
				imageOwners?: Record<string, string>;
			};
	  }
	| {
			type: 'DOWNLOAD_BACKGROUND_REPLICA_PAGES';
			payload: {
				pages: Array<string>;
				IssueID?: string | number;
			};
	  }
	| {
			// Deletes every cached asset (image/audio/video) attributed to
			// one issue, without touching any other issue's downloads or the
			// shared fonts/assets caches. Reserved for a real deliberate
			// wipe (a byte-level "forget this issue entirely" action, not
			// yet exposed anywhere) -- removing a downloaded issue itself
			// goes through UNPIN_ISSUE_ASSETS below instead, which keeps
			// the bytes.
			type: 'EVICT_ISSUE';
			payload: {
				IssueID: string | number;
			};
	  }
	| {
			// The explicit "download this issue for offline" action
			// (`entities/downloaded-issue`'s `saveDownloadedIssue`) --
			// guarantees every URL in `images` is cached and exempt from
			// `relieveStoragePressure`'s storage-pressure eviction, fetching
			// whichever ones aren't already sitting in `cf-images` from
			// incidental browsing. See `sw.ts`'s `pinIssueAssets`.
			type: 'PIN_ISSUE_ASSETS';
			payload: {
				images: Array<string>;
				IssueID: string | number;
			};
	  }
	| {
			// Reverses PIN_ISSUE_ASSETS for one issue when its download is
			// removed (from either the home screen's downloads drawer or the
			// issue reader's own "Remove downloaded issue") -- every cached byte stays exactly where
			// it is, this only clears the exemption so the assets become
			// ordinary, storage-pressure-evictable entries again. See
			// `sw.ts`'s `unpinIssueAssets`.
			type: 'UNPIN_ISSUE_ASSETS';
			payload: {
				IssueID: string | number;
			};
	  }
	| {
			// Deletes specific pinned assets that a "refresh content" pass
			// (`RefreshContentButton`) found no longer referenced by the
			// freshly-refetched issue -- a real, permanent delete (unlike
			// UNPIN_ISSUE_ASSETS), scoped to `IssueID` so a URL some other
			// issue also owns is left alone. See `sw.ts`'s
			// `pruneRemovedIssueAssets`.
			type: 'PRUNE_ISSUE_ASSETS';
			payload: {
				images: Array<string>;
				IssueID: string | number;
			};
	  }
	| {
			// Wipes every offline content cache (image/audio/video, pinned or
			// not) and the ownership index, without touching the app shell,
			// fonts, or shared assets. Sent on both login and logout
			// (`shared/auth/session-teardown.ts`'s `clearSessionCache`) --
			// most callers there aren't React components, so they post this
			// directly via `navigator.serviceWorker.controller` rather than
			// through this context; the message shape is declared here too
			// so it stays documented in one place. See `sw.ts`'s
			// `clearOfflineContent`.
			type: 'CLEAR_OFFLINE_CONTENT';
			payload?: undefined;
	  };

export interface ServiceWorkerContextType {
	serviceWorker: ServiceWorker | null;
	isReady: boolean;
	sendMessage: (message: ServiceWorkerMessage) => void;
	// Convenience wrapper around the EVICT_ISSUE message -- deletes one
	// issue's cached images/audio/video without touching any other issue or
	// the shared fonts/assets caches. Not called anywhere yet; exists so the
	// still-open eviction-policy and un-favorite-cleanup work (DASHBOARD.md
	// Section 02) has the capability ready rather than needing to add it.
	evictIssue: (issueId: string | number) => void;
	// Convenience wrapper around PIN_ISSUE_ASSETS -- see that message's own
	// doc comment. Called by `widgets/issue-reader/menu/menu-button.tsx`'s
	// `DownloadIssueButton` once `saveDownloadedIssue` (Dexie) succeeds.
	pinIssueAssets: (images: Array<string>, issueId: string | number) => void;
	// Convenience wrapper around UNPIN_ISSUE_ASSETS -- see that message's
	// own doc comment. Called wherever `removeDownloadedIssue` is (both
	// `DownloadIssueButton` and `entities/downloaded-issue`'s own
	// `useDownloadedIssues` remove flow, shared by `widgets/downloads-
	// drawer` and the reader's `RefreshContentButton`).
	unpinIssueAssets: (issueId: string | number) => void;
	// Convenience wrapper around PRUNE_ISSUE_ASSETS -- see that message's
	// own doc comment. Called by `RefreshContentButton` once a refresh
	// finds images the freshly-refetched issue no longer references.
	pruneRemovedIssueAssets: (
		images: Array<string>,
		issueId: string | number,
	) => void;
}

export const ServiceWorkerContext = createContext<
	ServiceWorkerContextType | undefined
>(undefined);

export const useServiceWorkerContext = () => {
	const context = useContext(ServiceWorkerContext);
	if (context === undefined) {
		throw new Error(
			'useServiceWorkerContext must be used within a ServiceWorkerProvider',
		);
	}
	return context;
};

interface ServiceWorkerProviderProps {
	children: ReactNode;
}

export const ServiceWorkerProvider: React.FC<ServiceWorkerProviderProps> = ({
	children,
}) => {
	const serviceWorker = useLoad();
	const isReady = serviceWorker !== null;

	const sendMessage = useCallback(
		(message: ServiceWorkerMessage) => {
			if (serviceWorker) {
				serviceWorker.postMessage(message);
			} else {
				logger.warn(
					'Service Worker not available to send message:',
					message,
				);
			}
		},
		[serviceWorker],
	);

	const evictIssue = useCallback(
		(issueId: string | number) => {
			sendMessage({ type: 'EVICT_ISSUE', payload: { IssueID: issueId } });
		},
		[sendMessage],
	);

	const pinIssueAssets = useCallback(
		(images: Array<string>, issueId: string | number) => {
			sendMessage({
				type: 'PIN_ISSUE_ASSETS',
				payload: { images, IssueID: issueId },
			});
		},
		[sendMessage],
	);

	const unpinIssueAssets = useCallback(
		(issueId: string | number) => {
			sendMessage({
				type: 'UNPIN_ISSUE_ASSETS',
				payload: { IssueID: issueId },
			});
		},
		[sendMessage],
	);

	const pruneRemovedIssueAssets = useCallback(
		(images: Array<string>, issueId: string | number) => {
			sendMessage({
				type: 'PRUNE_ISSUE_ASSETS',
				payload: { images, IssueID: issueId },
			});
		},
		[sendMessage],
	);

	const value: ServiceWorkerContextType = {
		serviceWorker,
		isReady,
		sendMessage,
		evictIssue,
		pinIssueAssets,
		unpinIssueAssets,
		pruneRemovedIssueAssets,
	};

	// Not every template's data hook explicitly pushes its images to the SW
	// for background caching (only a couple do, and only for cover/featured
	// thumbnails, not general article feeds) -- and passive caching via the
	// SW's own CacheFirst image route only works once the SW is actually
	// controlling the page, which it structurally isn't yet for the first
	// visit's initial navigation (a worker can't retroactively intercept the
	// load that triggered its own registration). Together those two gaps
	// meant most rendered content images were never available offline, even
	// right after being viewed. Generically scan the DOM for rendered <img>
	// src values instead, template-agnostic, and forward new ones to the SW
	// -- postMessage works regardless of controller/interception state, so
	// this covers the first-visit window too.
	const sentImagesRef = useRef<Set<string>>(new Set());
	useEffect(() => {
		if (!isReady) return;

		const collectAndSend = () => {
			const images = Array.from(document.images)
				.map((img) => img.currentSrc || img.src)
				.filter(
					(src) =>
						isExternalContentImage(src) &&
						!sentImagesRef.current.has(src),
				);

			if (!images.length) return;
			for (const src of images) {
				sentImagesRef.current.add(src);
			}
			sendMessage({
				type: 'DOWNLOAD_BACKGROUND_IMAGES',
				// These images are rendered in the DOM right now, unlike the
				// data-driven prefetch hooks (use-issue.ts, per-template
				// Home.hooks.ts, etc.) that send the same message type for
				// covers/thumbnails regardless of viewport — the SW's shared
				// download queue (src/sw.ts) uses this to let on-screen
				// images jump ahead of background prefetch.
				payload: { images, priority: 'high' },
			});
		};

		collectAndSend();

		let debounceId: ReturnType<typeof setTimeout> | undefined;
		const observer = new MutationObserver(() => {
			if (debounceId) clearTimeout(debounceId);
			debounceId = setTimeout(collectAndSend, 1500);
		});
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			// srcset alongside src: a responsive-image component can swap
			// resolutions (e.g. a lazy-loading component picking a new size)
			// without ever touching the src attribute itself. New elements are
			// still caught via childList regardless of which attribute they
			// arrive with set.
			attributeFilter: ['src', 'srcset'],
		});

		return () => {
			observer.disconnect();
			if (debounceId) clearTimeout(debounceId);
		};
	}, [isReady, sendMessage]);

	return (
		<ServiceWorkerContext.Provider value={value}>
			{children}
		</ServiceWorkerContext.Provider>
	);
};
